import React, { useState, useEffect } from 'react';
import { useLocation, useParams,useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Slider,
  Alert,
  Skeleton,
  CircularProgress,
  Fade,
  TextField,
  Fab
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RateReviewIcon from '@mui/icons-material/RateReview';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useBalance } from '../context/BalanceContext';
import { getAuth } from 'firebase/auth';
import app from './firebase';
import LazyPDFPage from './LazyPDFPage';
import { pdfjs } from 'react-pdf';


pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


// ScrollTop コンポーネント（一定スクロール後に表示されるトップへ戻るボタン）
const ScrollTop = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Fab
      color="primary"
      onClick={handleClick}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        display: visible ? 'flex' : 'none',
      }}
      aria-label="scroll back to top"
    >
      <KeyboardArrowUpIcon />
    </Fab>
  );
};

const DetailPage = () => {
  const { publicKey } = useWallet();
  const { tokenBalance } = useBalance();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth(app);

  // Firestore の評価状態
  const [hasEvaluated, setHasEvaluated] = useState(false);
  // location.state から paperData を取得。なければ後で Firestore から取得
  const [paperData, setPaperData] = useState(location.state?.paper);
  // Firebase Storage の利用
  const storage = getStorage();

  // サムネイルと PDF の URL、ロード状態
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(true);

  // PDF ドキュメントオブジェクトおよびページ数（Lazy Loading 用）
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);

  // モーダルの状態（評価入力、確認、残高不足、ウォレット未接続）
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [insufficientBalanceModalOpen, setInsufficientBalanceModalOpen] = useState(false);
  const [walletConnectModalOpen, setWalletConnectModalOpen] = useState(false);

  // 評価送信状態
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');

  // 評価スコア（各項目の初期値は 2）
  const [evaluationScores, setEvaluationScores] = useState({
    originality: 2,
    significance: 2,
    methodology: 2,
    clarity: 2,
    overall: 2,
  });
  const totalAllocated = Object.values(evaluationScores).reduce((a, b) => a + b, 0);

  // paperData が渡されていなければ Firestore から取得
  useEffect(() => {
    if (!paperData && id) {
      const firestore = getFirestore();
      const paperDocRef = doc(firestore, "papers", id);
      getDoc(paperDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            setPaperData(docSnap.data());
          } else {
            console.error("No such document in Firestore!");
          }
        })
        .catch((error) => {
          console.error("Error fetching paper from Firestore:", error);
        });
    }
  }, [paperData, id]);

  // paperData 利用可能時にサムネイルと PDF の URL を取得
  useEffect(() => {
    if (paperData) {
      const thumbRef = ref(storage, paperData.thumbnailFilePath);
      getDownloadURL(thumbRef)
        .then((url) => {
          setThumbnailUrl(url);
          setIsThumbnailLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching thumbnail URL:", error);
          setIsThumbnailLoading(false);
        });

      const pdfRef = ref(storage, paperData.pdfFilePath);
      getDownloadURL(pdfRef)
        .then((url) => {
          setPdfUrl(url);
        })
        .catch((error) => {
          console.error("Error fetching PDF URL:", error);
          setIsPdfLoading(false);
        });
    }
  }, [storage, paperData]);

  // pdfUrl 取得後、PDF ドキュメントを読み込み、ページ数をセット（Lazy Loading 用）
  useEffect(() => {
    if (pdfUrl) {
      setIsPdfLoading(true);
      fetch(pdfUrl)
        .then(response => response.blob())
        .then(async (blob) => {
          const arrayBuffer = await blob.arrayBuffer();
          const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
          setPdfDoc(pdf);
          setNumPages(pdf.numPages);
          setIsPdfLoading(false);
        })
        .catch((error) => {
          console.error("Error loading PDF document:", error);
          setIsPdfLoading(false);
        });
    }
  }, [pdfUrl]);

  // 現在のユーザーがこの論文を既に評価しているかどうかをチェック
  useEffect(() => {
    if (auth.currentUser && paperData) {
      const firestore = getFirestore();
      // ドキュメントID を `${userId}_${paperId}` としている前提
      const evalDocRef = doc(firestore, "evaluations", `${auth.currentUser.uid}_${(paperData.id || id)}`);
      getDoc(evalDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            setHasEvaluated(true);
          }
        })
        .catch((error) => {
          console.error("Error checking evaluation status:", error);
        });
    }
  }, [auth.currentUser, paperData, id]);

  // 評価の各項目
  const evaluationCriteria = [
    { key: 'originality', label: 'Originality' },
    { key: 'significance', label: 'Significance' },
    { key: 'methodology', label: 'Methodology' },
    { key: 'clarity', label: 'Clarity' },
    { key: 'overall', label: 'Overall Evaluation' },
  ];

  const [desc, setDesc] = useState('');

  const handleSliderChange = (key) => (event, newValue) => {
    setEvaluationScores((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const openEvaluationModal = () => {
    if (!publicKey) {
      setWalletConnectModalOpen(true);
      return;
    }
    if (tokenBalance < 50) {
      setInsufficientBalanceModalOpen(true);
      return;
    }
    // 既に評価済みの場合は何もしない
    if (hasEvaluated) return;
    setEvaluationModalOpen(true);
  };

  const closeEvaluationModal = () => {
    setEvaluationModalOpen(false);
  };

  const openConfirmationModal = () => {
    setEvaluationModalOpen(false);
    setConfirmationModalOpen(true);
  };

  const closeConfirmationModal = (reopenEvaluation = false) => {
    setConfirmationModalOpen(false);
    if (reopenEvaluation) {
      setEvaluationModalOpen(true);
    }
  };

  const closeInsufficientBalanceModal = () => {
    setInsufficientBalanceModalOpen(false);
  };

  const closeWalletConnectModal = () => {
    document.body.style.overflow = 'auto';
    setWalletConnectModalOpen(false);
  };

  // 評価送信処理（API 呼び出しを含む）
  const handleEvaluationSubmit = async () => {
    setSubmissionError('');
    setIsSubmitting(true);
    const idToken = await auth.currentUser.getIdToken();
    const payload = {
      userId: auth.currentUser ? auth.currentUser.uid : 'anonymous',
      paperId: paperData.id || id,
      evaluationScores,
      comment: desc,
      totalScore: totalAllocated,
    };

    try {
      const response = await fetch('/evaluate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${idToken}`,
          
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Evaluation submission failed');
      }
      setIsSubmitting(false);
      closeConfirmationModal();
      // 評価済み状態にする
      setHasEvaluated(true);
    } catch (error) {
      setIsSubmitting(false);
      setSubmissionError(error.message);
    }
  };

  const walletButtonStyle = { backgroundColor: '#666666' };

  // SNS 用のメタ情報（paperData が取得済みの場合はタイトルや説明、サムネイル URL を利用）
  const pageTitle = paperData ? paperData.title : 'Default Paper Title';
  const pageDescription = paperData
    ? (paperData.description || 'A detailed paper evaluation page.')
    : 'Default description for the paper evaluation page.';

  if (!paperData) {
    return (
      <Box sx={{ minHeight: '100vh', padding: 4, color: '#fff' }}>
        <Typography variant="h6">Loading paper data...</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Helmet によるメタタグ設定 */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={thumbnailUrl || 'http://127.0.0.1:5000/static/media/einstein.2347d921365dced24cfc.jpg'} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={thumbnailUrl || 'http://127.0.0.1:5000/static/media/einstein.2347d921365dced24cfc.jpg'} />
      </Helmet>

      {/* ページ本体 */}
      <Box sx={{ minHeight: '100vh', padding: 4, color: '#fff' }}>
      <Button
  variant="contained"
  onClick={() => navigate("/explore")}
  sx={{
    background: 'linear-gradient(45deg, #007acc, #005999)',
    color: '#fff',
    mb: 2
  }}
>
  ← Explore
</Button>
        <Grid container spacing={4} justifyContent="center">
       
          {/* サムネイル表示（3:2 のアスペクト比） */}
          <Grid item xs={12} sm={9} md={6}>
            <Paper
              elevation={3}
              sx={{
                position: 'relative',
                width: '100%',
                paddingTop: '66.66%',
                backgroundColor: '#222',
              }}
            >
              {isThumbnailLoading ? (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              ) : (
                <Fade in timeout={600}>
                  <Box
                    component="img"
                    src={thumbnailUrl}
                    alt={`${paperData.title} thumbnail`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Fade>
              )}
            </Paper>
          </Grid>

          {/* 論文詳細情報 */}
          <Grid item xs={12} sm={9} md={6}>
            <Card sx={{ backgroundColor: '#222' }}>
              <CardContent>
                <Typography gutterBottom variant="h4" component="div" sx={{ color: '#fff' }}>
                  {paperData.title}
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff' }} gutterBottom>
                  <strong>Created at:</strong> {`${Date(paperData.createdAt.seconds * 1000 + paperData.createdAt.nanoseconds / 1_000_000)}`}
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff' }} gutterBottom>
                  <strong>Transaction Signature:</strong> {paperData.transactionSignature}
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff' }} gutterBottom>
                  <strong>User ID:</strong> {paperData.user_id}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RateReviewIcon sx={{ color: '#fff', mr: 0.5 }} />
                    <Typography variant="body1" sx={{ color: '#fff' }}>
                      <strong>Evaluations:</strong> {paperData.evaluations}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <WorkspacePremiumIcon sx={{ color: '#fff', mr: 0.5 }} />
                    <Typography variant="body1" sx={{ color: '#fff' }}>
                      <strong>Points:</strong> {paperData.points}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 3 }}>
                  {hasEvaluated ? (
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#444',
                        color: '#fff',
                        width: '100%',
                        py: 1.5,
                      }}
                      disabled
                    >
                      Evaluated
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#444',
                        color: '#fff',
                        width: '100%',
                        py: 1.5,
                      }}
                      onClick={openEvaluationModal}
                    >
                      Evaluate
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* PDF 表示（Lazy Loading） */}
          <Grid item xs={12} sm={9} md={6}>
            <Card sx={{ backgroundColor: '#222' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#fff', marginBottom: 2 }}>
                  PAPER
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff', marginTop: 1 }}>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#fff', textDecoration: 'underline' }}
                  >
                    Open PDF in a new tab
                  </a>
                </Typography>
                {isPdfLoading ? (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      width: '100%',
                      height: 600,
                      marginTop: 2,
                    }}
                  />
                ) : (
                  <>
                    {Array.from({ length: numPages }, (_, index) => (
                      <LazyPDFPage key={index} pdf={pdfDoc} pageNumber={index + 1} scale={1} />
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* 評価入力モーダル */}
      <Dialog
        open={evaluationModalOpen}
        onClose={closeEvaluationModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { backgroundColor: '#222', color: '#fff' } }}
        TransitionComponent={Fade}
        transitionDuration={600}
      >
        <DialogTitle sx={{ color: '#fff' }}>
          Paper Evaluation (Each item out of 4, maximum total 20 points)
        </DialogTitle>
        <DialogContent dividers>
          {evaluationCriteria.map((criteria) => (
            <Box key={criteria.key} sx={{ mb: 3 }}>
              <Typography gutterBottom>{criteria.label}</Typography>
              <Slider
                value={evaluationScores[criteria.key]}
                onChange={handleSliderChange(criteria.key)}
                aria-labelledby={`${criteria.key}-slider`}
                valueLabelDisplay="auto"
                step={1}
                marks
                min={0}
                max={4}
              />
            </Box>
          ))}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">
              Current Total: {totalAllocated} / 20 points
            </Typography>
            {totalAllocated > 20 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                The total exceeds 20 points. Please adjust.
              </Alert>
            )}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>Comment</Typography>
            <TextField
              variant="outlined"
              multiline
              rows={4}
              fullWidth
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Comment to this paper"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEvaluationModal} sx={{ color: '#fff' }}>
            Cancel
          </Button>
          <Button
            onClick={openConfirmationModal}
            variant="contained"
            sx={{ backgroundColor: '#444', color: '#fff' }}
            disabled={totalAllocated > 20}
          >
            Submit Evaluation
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認モーダル */}
      <Dialog
        open={confirmationModalOpen}
        onClose={(event, reason) => {
          if (isSubmitting) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          closeConfirmationModal(true);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { backgroundColor: '#222', color: '#fff' } }}
        TransitionComponent={Fade}
        transitionDuration={600}
      >
        <DialogTitle sx={{ color: '#fff' }}>Confirm Evaluation Submission</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to submit your evaluation? Upon submission, you will receive <strong>0.02 EMCII</strong>.
          </Typography>
          <Typography variant="body1">
            Additionally, if this paper receives a high evaluation, you will be rewarded with a significant bonus!
          </Typography>
          {submissionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submissionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeConfirmationModal(true)} sx={{ color: '#fff' }} disabled={isSubmitting}>
            Back
          </Button>
          <Button
            onClick={handleEvaluationSubmit}
            variant="contained"
            sx={{ backgroundColor: '#444', color: '#fff' }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            ) : (
              'Confirm Submission'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 残高不足モーダル */}
      <Dialog open={insufficientBalanceModalOpen} onClose={closeInsufficientBalanceModal} fullWidth maxWidth="sm">
        <DialogTitle>Insufficient Balance</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            You need at least <strong>50 EMCII</strong> to evaluate this paper. Your current balance is {tokenBalance} EMCII.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeInsufficientBalanceModal} variant="contained" sx={{ backgroundColor: '#444', color: '#fff' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Wallet Connect モーダル */}
      <Dialog open={walletConnectModalOpen} onClose={closeWalletConnectModal} fullWidth maxWidth="sm">
        <DialogTitle>You need connect your wallet</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Connect your wallet
          </DialogContentText>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }} onClick={closeWalletConnectModal}>
            <WalletMultiButton style={walletButtonStyle} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeWalletConnectModal} variant="contained" sx={{ backgroundColor: '#444', color: '#fff' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* スクロールトップボタン */}
      <ScrollTop />
    </>
  );
};

export default DetailPage;
